// A2pSetupPanel — Settings + Onboarding surface for A2P 10DLC.
//
// Sits alongside the KixiePowerDialPanel + ImessagePanel in
// Settings → Comms & Calendar. Its job:
//
//   1. Collect the business info Twilio's A2P campaign registration
//      requires (business_name, business_website, business_ein,
//      business_address). Persist via /api/twilio/a2p-business-info.
//   2. Show live status: pending / approved / rejected / needs_info.
//      Polls /api/twilio/a2p-business-info GET on mount + after save.
//   3. Nudge with the correct next step: "Enter your info" →
//      "Under review (1–5 days)" → "Approved — you can text."
//
// Why this UI is required even though ASCEND is autonomous:
//   The ASCEND `a2p_submitted` step + the `a2p-sweep` cron were
//   already wired to CREATE the per-tenant campaign, but they read
//   subscriptions.business_name / business_website — which no
//   signup surface was populating. Result: campaigns went to Twilio
//   with generic placeholder text. This panel is the collection
//   step that was structurally missing from the wizard.

import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import { supabase } from '../lib/supabase';

async function bearerHeaders() {
  try {
    const token = readPersistedJwtDirect();
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch { return {}; }
}

function StatusBadge({ status }) {
  const map = {
    approved:  { bg: 'bg-emerald-100', fg: 'text-emerald-800', label: 'Approved · SMS live' },
    pending:   { bg: 'bg-amber-100',   fg: 'text-amber-800',   label: 'Under review · 1–5 days' },
    submitted: { bg: 'bg-amber-100',   fg: 'text-amber-800',   label: 'Under review · 1–5 days' },
    in_review: { bg: 'bg-amber-100',   fg: 'text-amber-800',   label: 'Under review · 1–5 days' },
    failed:    { bg: 'bg-red-100',     fg: 'text-red-800',     label: 'Rejected · fix + resubmit' },
    rejected:  { bg: 'bg-red-100',     fg: 'text-red-800',     label: 'Rejected · fix + resubmit' },
  };
  const s = map[status] || { bg: 'bg-slate-100', fg: 'text-slate-700', label: 'Not submitted' };
  return (
    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${s.bg} ${s.fg}`}>
      {s.label}
    </span>
  );
}

export default function A2pSetupPanel() {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [ok, setOk] = React.useState(false);
  const [status, setStatus] = React.useState(null);
  const [lastError, setLastError] = React.useState(null);

  const [businessName, setBusinessName] = React.useState('');
  const [businessWebsite, setBusinessWebsite] = React.useState('');
  const [businessAddress, setBusinessAddress] = React.useState('');
  const [businessEin, setBusinessEin] = React.useState('');
  const [einPresent, setEinPresent] = React.useState(false);
  // Readiness review from /api/onboarding/a2p-readiness — wraps the
  // agent-tool a2p-review scorer + rejection explainer so the
  // subscriber sees a live score + checklist + plain-English
  // rejection reason. Best-effort — panel still works if this
  // returns { available: false } (falls back to raw carrier error).
  const [review, setReview] = React.useState(null);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = await bearerHeaders();
      const res = await fetch('/api/twilio/a2p-business-info', { headers });
      const body = await res.json();
      if (!res.ok || !body.ok) {
        setError(body.error || `status_${res.status}`);
      } else {
        setBusinessName(body.business_name || '');
        setBusinessWebsite(body.business_website || '');
        setBusinessAddress(body.business_address || '');
        setEinPresent(!!body.business_ein_present);
        setStatus(body.a2p_campaign_status);
        setLastError(body.a2p_last_error || null);
      }
      // Readiness review is a best-effort enhancement — fire in
      // parallel-ish but don't fail the whole panel if the wrapper
      // returns { available: false } (missing AGENT_TOOL_SECRET,
      // upstream tool 500s, etc.).
      try {
        const rr = await fetch('/api/onboarding/a2p-readiness', { headers });
        const rb = await rr.json();
        if (rb && rb.available) setReview(rb);
        else setReview(null);
      } catch (_) { /* silent — no readiness card renders */ }
    } catch (err) {
      setError(err.message || 'network_error');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { refresh(); }, [refresh]);

  const canSubmit = businessName.trim().length >= 2 && !busy;

  const save = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    setOk(false);
    try {
      const headers = { ...(await bearerHeaders()), 'content-type': 'application/json' };
      const payload = {
        business_name:    businessName.trim(),
        business_website: businessWebsite.trim() || null,
        business_address: businessAddress.trim() || null,
      };
      // Only send business_ein if the field is non-empty AND was
      // typed this session. If einPresent && ein is empty, we leave
      // the existing DB value alone.
      if (businessEin.trim()) payload.business_ein = businessEin.trim();

      const res = await fetch('/api/twilio/a2p-business-info', {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });
      const body = await res.json();
      if (!res.ok || !body.ok) {
        setError(body.error || `save_${res.status}`);
      } else {
        setOk(true);
        setBusinessEin(''); // clear the input; the DB value persists
        await refresh();
      }
    } catch (err) {
      setError(err.message || 'network_error');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="text-sm text-slate-500">Loading A2P status…</div>
      </div>
    );
  }

  const isApproved = status === 'approved';

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-1">
        <div className="text-sm font-bold text-slate-900">A2P 10DLC · SMS carrier verification</div>
        <StatusBadge status={status}/>
      </div>
      <div className="text-[11px] text-slate-500 mb-3">
        US carriers require registered businesses to send bulk SMS. Twilio submits your info; carriers approve within 1–5 business days. Until approved, sends use the platform's shared brand.
      </div>

      {isApproved ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 space-y-1">
          <div className="text-xs font-bold text-emerald-900">You're approved</div>
          <div className="text-[11px] text-emerald-800">
            Your outbound SMS rides your own registered brand + campaign. No further action needed.
          </div>
          {businessName && (
            <div className="text-[10px] text-emerald-700 mt-1">
              Business: {businessName}
            </div>
          )}
        </div>
      ) : (
        <form onSubmit={save} className="space-y-3">
          {/* Rejection explainer — prefer the plain-English
              explanation + fixes from the a2p-review agent-tool if
              available, otherwise fall back to the raw carrier
              string (matches pre-existing behavior). */}
          {(status === 'failed' || status === 'rejected') && (
            <div className="bg-red-50 border border-red-200 rounded-md p-2 text-xs text-red-800">
              <div className="font-semibold">Why the carrier rejected it:</div>
              <div className="text-[11px] mt-1">
                {(review && review.rejection && review.rejection.explanation)
                  || lastError
                  || 'The carrier needs more or clearer business information.'}
              </div>
              {review && review.rejection && Array.isArray(review.rejection.fixes) && review.rejection.fixes.length > 0 && (
                <ul className="text-[11px] mt-2 list-disc list-inside space-y-0.5">
                  {review.rejection.fixes.slice(0, 4).map((f, i) => (
                    <li key={i}><span className="font-semibold">Do:</span> {f}</li>
                  ))}
                </ul>
              )}
              <div className="text-[10px] mt-1 text-red-600">Update the fields below and resubmit.</div>
            </div>
          )}

          {/* Readiness scorecard — surfaces the agent-tool
              a2p-review scorer so subscribers see gaps BEFORE
              submitting. Renders only when the wrapper endpoint
              returned data (available=true) AND the account isn't
              already approved. */}
          {review && Array.isArray(review.checks) && review.checks.length > 0 && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-bold text-slate-800">Submission readiness</div>
                <div
                  className={`text-xs font-black tabular-nums ${
                    review.score >= 90 ? 'text-emerald-700'
                      : review.score >= 60 ? 'text-amber-700'
                      : 'text-red-700'
                  }`}
                >
                  {review.score}%
                  {review.band && (
                    <span className="ml-1 text-[10px] uppercase tracking-wider font-semibold text-slate-500">
                      · {String(review.band).replace(/_/g, ' ')}
                    </span>
                  )}
                </div>
              </div>
              <div className="space-y-1">
                {review.checks.map((c, i) => (
                  <div key={c.key || i} className="flex items-start gap-2 text-[11px]">
                    <span className={c.ok ? 'text-emerald-600' : 'text-slate-400'}>
                      {c.ok ? '✓' : '○'}
                    </span>
                    <div className="flex-1">
                      <div className={c.ok ? 'text-slate-600' : 'text-slate-800 font-medium'}>
                        {c.label || c.key}
                      </div>
                      {!c.ok && c.fix && (
                        <div className="text-[10px] text-slate-500 mt-0.5">→ {c.fix}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {review.next_step && (
                <div className="text-[11px] text-slate-700 mt-2 pt-2 border-t border-slate-200">
                  <span className="font-semibold">Next:</span> {String(review.next_step).replace(/_/g, ' ')}
                </div>
              )}
            </div>
          )}

          <label className="block">
            <div className="text-xs font-semibold text-slate-700 mb-1">
              Business name <span className="text-red-500">*</span>
            </div>
            <input
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              type="text"
              placeholder="Your Agency LLC"
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
              disabled={busy}
              maxLength={200}
            />
            <div className="text-[10px] text-slate-500 mt-1">
              Legal business name. Must match your EIN filing or Twilio will reject.
            </div>
          </label>

          <label className="block">
            <div className="text-xs font-semibold text-slate-700 mb-1">
              Business website
            </div>
            <input
              value={businessWebsite}
              onChange={(e) => setBusinessWebsite(e.target.value)}
              type="url"
              placeholder="https://youragency.com"
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
              disabled={busy}
            />
            <div className="text-[10px] text-slate-500 mt-1">
              Recommended. Carriers verify the domain has an opt-in mechanism (contact form / booking page).
            </div>
          </label>

          <label className="block">
            <div className="text-xs font-semibold text-slate-700 mb-1">
              EIN {einPresent && <span className="text-emerald-600 font-normal">(on file — leave blank to keep)</span>}
            </div>
            <input
              value={businessEin}
              onChange={(e) => setBusinessEin(e.target.value)}
              type="text"
              placeholder={einPresent ? '••-•••••••' : '12-3456789'}
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm font-mono"
              disabled={busy}
              maxLength={12}
              autoComplete="off"
            />
            <div className="text-[10px] text-slate-500 mt-1">
              9 digits. Not shown after save.
            </div>
          </label>

          <label className="block">
            <div className="text-xs font-semibold text-slate-700 mb-1">
              Business address
            </div>
            <textarea
              value={businessAddress}
              onChange={(e) => setBusinessAddress(e.target.value)}
              placeholder="123 Main St · Suite 400 · Austin, TX 78701"
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
              disabled={busy}
              rows={2}
              maxLength={500}
            />
          </label>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-2 text-xs text-red-800">
              {error}
            </div>
          )}

          {ok && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-md p-2 text-xs text-emerald-800">
              Saved. Twilio submits to carriers on the next sweep (within 15 min).
            </div>
          )}

          <button
            type="submit"
            disabled={!canSubmit}
            className={`w-full py-2 rounded-md text-sm font-semibold ${
              canSubmit
                ? 'bg-slate-900 text-white hover:bg-slate-800'
                : 'bg-slate-200 text-slate-500 cursor-not-allowed'
            }`}
          >
            {busy ? 'Saving…' : (status ? 'Update & resubmit' : 'Save & submit to carriers')}
          </button>
        </form>
      )}
    </div>
  );
}
